// Client-side find-as-you-type over the motion-period list: Google-style all-words
// matching over the text a viewer can actually read (period descriptions + clip
// observation text + people phrases), with <mark> highlighting. Everything runs in
// the browser over rows already fetched for the selected window — no server search.

import type { ReactElement } from "react";
import type { MotionPeriodResponse, SensoryLogResponse } from "../../lib/api";
import {
  observationDisplayText,
  parsePersonRecords,
  peoplePills,
  substituteEntityIds,
} from "./periodText";

// Google's quoting convention: "woman in black" is one token that must occur
// contiguously (substring matching gives contiguity for free); bare words match
// anywhere. An unterminated quote reads as phrase-to-end, so typing a quote never
// breaks the search mid-keystroke.
export const tokenize = (query: string): string[] => {
  const balanced = (query.match(/"/g) ?? []).length % 2 === 0 ? query : `${query}"`;
  const tokens: string[] = [];
  for (const m of balanced.toLowerCase().matchAll(/"([^"]*)"|(\S+)/g)) {
    const t = (m[1] ?? m[2] ?? "").replace(/\s+/g, " ").trim();
    if (t) tokens.push(t);
  }
  return tokens;
};

export const matchesAll = (haystack: string, tokens: string[]): boolean => {
  const lower = haystack.toLowerCase();
  return tokens.every((t) => lower.includes(t));
};

// The searchable corpus for one period: the prose a viewer reads in the pane, plus
// the reason and (when loaded) the alert decision's text — owners receive those in
// their alert notification and search the words they were sent. Raw and
// entity-substituted descriptions are both included so a hit is findable whichever
// form the row happens to display; machine entity entries are excluded in favour of
// their rendered people phrases.
export const periodSearchText = (
  period: MotionPeriodResponse,
  observations: SensoryLogResponse[] | undefined,
  alertText?: string | null,
): string => {
  const parts: string[] = [];
  if (period.description) parts.push(period.description);
  if (period.reason) parts.push(period.reason);
  if (alertText) parts.push(alertText);
  if (observations !== undefined && observations.length > 0) {
    const records = parsePersonRecords(observations);
    if (period.description && records.length > 0)
      parts.push(substituteEntityIds(period.description, records));
    parts.push(...peoplePills(records));
    for (const o of observations) {
      const text = observationDisplayText(o);
      if (text) parts.push(text);
    }
  }
  return parts.join("\n");
};

// Why a matching row shows no highlighted word: the hit lives in text the collapsed
// row doesn't display. Tiered by how directly the owner can see it after expanding —
// the reason line renders in the expanded row, the alert notification text and clip
// observations may not.
export const hiddenMatchLabel = (
  period: MotionPeriodResponse,
  alertText: string | null | undefined,
  tokens: string[],
): "in reason" | "in alert" | "in clips" | null => {
  if (tokens.length === 0) return null;
  if (matchesAll(period.description ?? "", tokens)) return null;
  const withReason = `${period.description ?? ""}\n${period.reason ?? ""}`;
  if (matchesAll(withReason, tokens)) return "in reason";
  if (alertText && matchesAll(`${withReason}\n${alertText}`, tokens)) return "in alert";
  return "in clips";
};

const escapeRegExp = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const Highlighted = ({ text, query }: { text: string; query: string }): ReactElement => {
  const tokens = tokenize(query);
  if (tokens.length === 0 || !text) return <>{text}</>;
  // Phrase intent: where the whole query occurs contiguously, mark it as ONE phrase
  // rather than scattered fragments. The full-query phrase goes first (regex
  // alternation is leftmost-first), then tokens longest-first so no token shadows a
  // longer one it prefixes.
  const alternatives = [...tokens].sort((a, b) => b.length - a.length);
  const phrase = tokens.join(" ");
  if (tokens.length > 1) alternatives.unshift(phrase);
  const re = new RegExp(`(${alternatives.map(escapeRegExp).join("|")})`, "gi");
  const parts = text.split(re);
  if (parts.length === 1) return <>{text}</>;
  return (
    <>
      {parts.map((part, i) =>
        // Odd indices are the capture-group matches produced by split().
        i % 2 === 1 ? (
          // biome-ignore lint/suspicious/noArrayIndexKey: parts are positional by construction
          <mark key={i}>{part}</mark>
        ) : (
          part
        ),
      )}
    </>
  );
};
